<template>
  <div
    class="floor-plan-map-view"
    :class="{ 'floor-plan-map-view--embedded-large': embeddedLarge }"
  >
    <div
      v-if="tooltip.visible && tooltipLine"
      class="floor-map-tooltip"
      :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
    >
      {{ tooltipLine }}
    </div>

    <div class="floor-plan-map-view__body">
      <div v-if="loading" class="floor-plan-map-view__loading">
        <md-icon class="md-size-2x">hourglass_empty</md-icon>
        <span>Loading map</span>
      </div>

      <p v-else-if="errorMessage" class="floor-plan-map-view__error">
        {{ errorMessage }}
      </p>

      <Zoomable v-else class="floor-plan-map-view__zoom">
        <svg
          ref="svgRef"
          preserveAspectRatio="xMidYMid meet"
          :viewBox="svgViewBox"
          class="floor-plan-map-view__svg"
        >
          <g v-html="svgText" />

          <g v-if="markerVisible">
            <circle
              :cx="markerCx"
              :cy="markerCy"
              r="5"
              :fill="markerColor"
              stroke="black"
              stroke-width="1"
              :style="tooltip.visible ? { filter: 'brightness(0.7)' } : {}"
              @mouseenter="showTooltip"
              @mouseleave="hideTooltip"
            />
          </g>
        </svg>
      </Zoomable>
    </div>
  </div>
</template>

<script>
import Zoomable from "@/components/Map/Zoomable.vue";
import {
  joinUrl,
  normalizeFloorsList,
  parseFloorSvgFetched,
} from "@/components/Map/floorPlanMapShared.js";

export default {
  name: "FloorPlanMapView",
  components: {
    Zoomable,
  },
  props: {
    buildingId: {
      type: [Number, String],
      default: null,
    },
    floorId: {
      type: [Number, String],
      default: null,
    },
    markerX: {
      type: [Number, String],
      default: null,
    },
    markerY: {
      type: [Number, String],
      default: null,
    },
    markerColor: {
      type: String,
      default: "#e53935",
    },
    tooltipLine: {
      type: String,
      default: "",
    },
    /** Wider / taller layout for installer row expansion (match print ticket area). */
    embeddedLarge: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      svgText: "",
      svgViewBox: "0 0 612 792",
      coordinateViewBox: "0 0 612 792",
      errorMessage: "",
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
      },
    };
  },
  computed: {
    parsedSvgViewBox() {
      return this.parseViewBox(this.svgViewBox);
    },
    parsedCoordinateViewBox() {
      return this.parseViewBox(this.coordinateViewBox);
    },
    markerCx() {
      const x = this.toNum(this.markerX);
      return this.remapAxis(
        x,
        this.parsedCoordinateViewBox,
        this.parsedSvgViewBox,
        "x",
      );
    },
    markerCy() {
      const y = this.toNum(this.markerY);
      return this.remapAxis(
        y,
        this.parsedCoordinateViewBox,
        this.parsedSvgViewBox,
        "y",
      );
    },
    markerVisible() {
      const x = this.markerCx;
      const y = this.markerCy;
      return x != null && y != null;
    },
  },
  watch: {
    buildingId: "reloadMap",
    floorId: "reloadMap",
    markerX: "reloadMap",
    markerY: "reloadMap",
  },
  mounted() {
    this.loadSvg();
  },
  methods: {
    reloadMap() {
      this.resetContent();
      this.loadSvg();
    },
    toNum(v) {
      if (v === null || v === undefined || v === "") {
        return null;
      }
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    },
    resetContent() {
      this.errorMessage = "";
      this.svgText = "";
      this.svgViewBox = "0 0 612 792";
      this.coordinateViewBox = "0 0 612 792";
      this.loading = false;
      this.hideTooltip();
    },
    parseViewBox(vb) {
      const parts = String(vb || "")
        .trim()
        .split(/\s+/)
        .map(Number);
      if (
        parts.length !== 4 ||
        !parts.every((n) => Number.isFinite(n)) ||
        parts[2] <= 0 ||
        parts[3] <= 0
      ) {
        return { minX: 0, minY: 0, width: 612, height: 792 };
      }
      return {
        minX: parts[0],
        minY: parts[1],
        width: parts[2],
        height: parts[3],
      };
    },
    remapAxis(value, fromVb, toVb, axis) {
      if (value == null) {
        return null;
      }
      const isX = axis === "x";
      const fromMin = isX ? fromVb.minX : fromVb.minY;
      const fromSize = isX ? fromVb.width : fromVb.height;
      const toMin = isX ? toVb.minX : toVb.minY;
      const toSize = isX ? toVb.width : toVb.height;
      if (fromSize <= 0 || toSize <= 0) {
        return value;
      }
      const ratio = toSize / fromSize;
      return toMin + (value - fromMin) * ratio;
    },
    showTooltip(event) {
      this.tooltip = {
        visible: true,
        x: event.clientX + 10,
        y: event.clientY + 10,
      };
    },
    hideTooltip() {
      this.tooltip.visible = false;
    },
    async loadSvg() {
      this.errorMessage = "";
      this.svgText = "";

      const bx = this.toNum(this.markerX);
      const by = this.toNum(this.markerY);
      if (this.buildingId == null || this.floorId == null) {
        this.errorMessage = "Building or floor is missing for this item.";
        return;
      }
      if (bx == null || by == null) {
        this.errorMessage = "Map coordinates are missing for this item.";
        return;
      }

      this.loading = true;
      try {
        const { data } = await this.$axios.get(
          `buildings/${this.buildingId}/floors`,
        );
        const rawFloors = Array.isArray(data)
          ? data
          : data && typeof data === "object" && data.floors !== undefined
          ? data.floors
          : data;
        const floors = normalizeFloorsList(rawFloors);
        const floor = floors.find((f) => Number(f.id) === Number(this.floorId));

        if (!floor || !floor.map) {
          this.errorMessage = "Floor plan was not found for this building.";
          return;
        }

        const mapUrl = joinUrl(floor.map);
        const res = await fetch(mapUrl);
        if (!res.ok) {
          throw new Error("Floor plan could not be loaded.");
        }
        const rawSvg = await res.text();
        const parsed = parseFloorSvgFetched(rawSvg);
        if (!parsed.inner) {
          this.errorMessage =
            parsed.parseError === "invalid_svg"
              ? "Floor plan file is not valid SVG."
              : "Floor plan file was empty or could not be read.";
          return;
        }
        this.svgText = parsed.inner;
        this.svgViewBox = parsed.viewBox;
      } catch (e) {
        this.errorMessage =
          (e && e.message) || "Unable to load the floor plan.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.floor-plan-map-view__body {
  min-height: 120px;
  max-width: min(96vw, 920px);
}

.floor-plan-map-view--embedded-large .floor-plan-map-view__body {
  max-width: none;
  width: 100%;
}

.floor-plan-map-view--embedded-large >>> .zoomable {
  max-height: 88vh;
  min-height: 72vh;
  width: 100%;
}

.floor-plan-map-view--embedded-large .floor-plan-map-view__zoom {
  width: 100%;
}

.floor-plan-map-view--embedded-large .floor-plan-map-view__svg {
  width: 100%;
  height: auto;
  min-height: 65vh;
  max-width: none;
}

.floor-plan-map-view__loading {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 24px;
}

.floor-plan-map-view__error {
  color: #b00020;
  margin: 12px 0;
}

.floor-plan-map-view__zoom {
  display: flex;
  justify-content: center;
}

.floor-plan-map-view__svg {
  border: 1px dashed #9e9e9e;
  max-width: 100%;
  min-width: 200px;
  min-height: 200px;
  background: #eceff1;
  color: #333;
}

.floor-map-tooltip {
  position: fixed;
  z-index: 1300;
  border: 1px solid #000;
  background: #fff;
  color: #111;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
}
</style>
